/**
 * Sprint 24 — EndpointUrlCard.
 *
 * Shared detail-page card showing a connection's isolated subset endpoint URL
 * (`/mcp/m/<id>` or `/mcp/w/<slug>`) with a copy button. Explains the tool-count
 * -cap workaround story. Used by both MCP and Wrapped detail pages.
 */

import { useState, type CSSProperties } from "react";
import { workerOrigin } from "../../../hooks/use-connections";
import { showToast } from "../primitives";

export interface EndpointUrlCardProps {
  /** Relative path, e.g. `/mcp/m/conn_abc`. The full origin is prepended. */
  url: string;
  explainer?: string;
}

const DEFAULT_EXPLAINER =
  "Point a host with a small tool budget (e.g. ChatGPT) at this endpoint to surface only " +
  "this connection's tools plus identity/session — sidesteps tool-count caps.";

export function EndpointUrlCard({ url, explainer = DEFAULT_EXPLAINER }: EndpointUrlCardProps) {
  const [copied, setCopied] = useState(false);
  const fullUrl = `${workerOrigin}${url}`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      showToast("Endpoint URL copied", "success");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      showToast("Couldn't copy — select and copy manually", "error");
    }
  }

  return (
    <div>
      <div style={card}>
        <i className="ti ti-link" style={{ fontSize: 13, color: "var(--color-text-tertiary)" }} />
        <span style={urlText} title={fullUrl}>
          {fullUrl}
        </span>
        <button type="button" style={copyBtn} onClick={copy}>
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <p style={explainerStyle}>{explainer}</p>
    </div>
  );
}

const card: CSSProperties = {
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-md)",
  border: "0.5px solid var(--color-border-secondary)",
  padding: "10px 14px",
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: 12,
  fontFamily: "var(--font-sans)",
};

const urlText: CSSProperties = {
  flex: 1,
  fontFamily: "var(--font-mono)",
  fontSize: 11,
  color: "var(--color-text-secondary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const copyBtn: CSSProperties = {
  fontSize: 11,
  padding: "4px 10px",
  borderRadius: "var(--border-radius-sm)",
  border: "0.5px solid var(--color-border-secondary)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  flexShrink: 0,
};

const explainerStyle: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  marginTop: 4,
  lineHeight: 1.5,
};
